import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import cn from "classnames";
import Button from "../common/Button";

type Flag = {
  id: string;
  user_id: string;
  level: "none" | "watch" | "throttled" | "suspended";
  reason: string | null;
  signals: (Record<string, number> & { scope?: "pool" | "personal" }) | null;
  auto_flagged: boolean;
  flagged_at: string;
  email: string;
  name: string | null;
  agency_name: string | null;
};

// Staff-portal view of Investigations abuse flags (seat sharing / volume).
// Auto-flags only ever reach watch/throttled; suspend is staff-only.
const InvestigationAbusePanel: React.FC<{ adminEmail: string }> = ({ adminEmail }) => {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get<Flag[]>("/api/agency-management/investigations/abuse-flags");
      setFlags(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setLevel = async (flag: Flag, level: string) => {
    const reason = level === "none" ? window.prompt("Note (why cleared)", "") : window.prompt("Reason", flag.reason || "");
    if (reason == null) return;
    try {
      await axios.post(`/api/agency-management/investigations/users/${flag.user_id}/flag`, { adminEmail, level, reason });
      toast.success("Updated");
      load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed");
    }
  };

  if (loading) return null;
  return (
    <div className="mt-8 rounded-xl border border-base-300 bg-base-100 p-4">
      <h2 className="text-lg font-semibold text-base-content mb-1">Investigations abuse flags</h2>
      <p className="text-xs text-base-content/60 mb-3">Accounts where several sessions upload jail calls at once, or usage is far over the seat allowance. Throttling limits ingestion only; nothing is deleted and chat/playback keep working.</p>
      {flags.length === 0 ? (
        <p className="text-sm text-base-content/60">No active flags.</p>
      ) : (
        <table className="table table-sm">
          <thead><tr><th>User</th><th>Agency</th><th>Level</th><th>Reason</th><th>Signals</th><th>Since</th><th /></tr></thead>
          <tbody>
            {flags.map((f) => (
              <tr key={f.id}>
                <td><div className="font-medium">{f.name || "—"}</div><div className="text-xs text-base-content/60">{f.email}</div></td>
                <td className="text-sm">{f.agency_name || "individual"}</td>
                <td>
                  <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full", f.level === "suspended" || f.level === "throttled" ? "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400" : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400")}>
                    {f.level}{f.auto_flagged ? "" : " (manual)"}
                  </span>
                </td>
                <td className="text-xs max-w-xs">{f.reason}</td>
                <td className="text-[11px] text-base-content/60">
                  {f.signals && (
                    <>
                      {f.signals.scope === "pool" ? (
                        <>dept {Math.round((f.signals.poolMinutes || 0) / 60)}/{Math.round((f.signals.poolAllowanceMinutes || 0) / 60)} hrs ({f.signals.overAllowancePct}%) · this user {Math.round((f.signals.audioMinutes || 0) / 60)} hrs ({f.signals.personalPct ?? "—"}% of a seat)</>
                      ) : (
                        <>{Math.round((f.signals.audioMinutes || 0) / 60)}/{Math.round((f.signals.allowanceMinutes || 0) / 60)} hrs · {f.signals.callsUploaded}/{f.signals.allowanceCalls} calls · {f.signals.overAllowancePct}%</>
                      )}
                      {" · "}{f.signals.simultaneousSessions} simult. sessions · {f.signals.distinctSubnets24h} subnets/24h · {f.signals.distinctUserAgents7d} UAs/7d
                    </>
                  )}
                </td>
                <td className="text-xs">{new Date(f.flagged_at).toLocaleDateString()}</td>
                <td className="whitespace-nowrap space-x-1">
                  {f.level !== "throttled" && <Button size="xs" variant="outline" onClick={() => setLevel(f, "throttled")}>Throttle</Button>}
                  {f.level !== "suspended" && <Button size="xs" variant="danger" onClick={() => setLevel(f, "suspended")}>Suspend</Button>}
                  <Button size="xs" variant="outline" onClick={() => setLevel(f, "none")}>Clear</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default InvestigationAbusePanel;
